import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Content from "../../layout/content/Content";
import {
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Badge,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
} from "reactstrap";
import Head from "../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Table,
  Collapse as AntCollapse,
  TableColumnsType,
  Descriptions,
  Modal,
  Timeline,
  Popover,
  Empty,
  Flex,
  Spin,
  Button as AntButton,
  Tag,
  Form,
  Input,
} from "antd";
import {
  PaperClipOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  CheckOutlined,
  MoreOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import WorkBoqAside from "./workBoqAside";
import { formatDecimal } from "../../utils/Utils";
import WorkBoqPdfGenerator from "./WorkBoqPdfGenerator";
import { useSelector } from "react-redux";

import workBoqApis from "../../api/WorkOrderManagement/WorkBOQ";
import { message } from "antd";
import { Link } from "react-router-dom";
import SignatureModal from "../components/SignatureModal";
import ImageUploadCropper from "../components/ImageUploadCropper";

const { Panel } = AntCollapse;

interface WorkBoqViewData {
  id: string;
  boqTitle: string;
  boqDescription: string;
  siteName: string;
  projectName?: string;
  contractorName?: string;
  workLevel: string;
  materialBoqName: string;
  budgetName?: string;
  completionAlertsFrom?: string;
  status: "Draft" | "Pending Approval" | "Approved" | "Rejected";
  createdAt: string;
  createdBy: string;
  submittedAt?: string;
  submittedBy?: string;
  approvals?: any[];
  rejectedReason?: string;
  workOrderLines: any[];
  instructions?: string;
  attachments?: any;
  approvalStatus?: string;
  isStrictApproval?: boolean;
}

const WorkBoqView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  // Get flowType from URL
  const searchParams = new URLSearchParams(location.search);
  const flowType = searchParams.get("flowType");
  const isClientFlow = flowType?.toLowerCase() === "client";
  const [workBoqData, setWorkBoqData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sm, setSm] = useState(false);
  const [tab, setTab] = useState("1");
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const [disableApprove, setDisableApprove] = useState(false);
  const [disableReject, setDisableReject] = useState(false);
  const [shareOptionsToggle, setShareOptionsToggle] = useState(false);
  const [instructionsVisible, setInstructionsVisible] = useState(false);

  // Signature & modal state (matching WO pattern)
  const [form] = Form.useForm();
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signature, setSignature] = useState<any>({
    isActive: false,
    label: "Authorised Signatory",
    image: null,
  });
  const [activeSignatureContext, setActiveSignatureContext] = useState<
    "APPROVE" | "REJECT" | null
  >(null);
  const [pendingRejectReason, setPendingRejectReason] = useState<string>("");
  const [isModalApproveVisible, setIsModalApproveVisible] = useState(false);
  const [isModalRejectedVisible, setIsModalRejectedVisible] = useState(false);

  // Mock current user permissions
  const [allowedActions] = useState({
    view: 1,
    edit: 1,
    approve: 1,
    reject: 1,
  });

  const currentUserId = currentUser?.id;
  const isStrictMode = workBoqData?.isStrictApproval || false;

  useEffect(() => {
    fetchWorkBoqDetails();
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, [id]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  const fetchWorkBoqDetails = async () => {
    try {
      if (!id) {
        message.error("Work BOQ ID is required");
        setIsLoading(false);
        return;
      }

      // Call the actual API
      const response: any = await workBoqApis.getWorkBoqById(id);

      if (
        response &&
        response.status &&
        response.data &&
        response.data.workBoq
      ) {
        const apiData = response.data.workBoq;

        // Map work order lines with proper structure
        const mappedWorkOrderLines =
          apiData.workBoqLines?.map((line: any) => ({
            id: line.workBoqLineId?.toString() || "",
            workCategory: line.workCategoryId?.toString() || "",
            workCategoryName: line.workCategoryName || "",
            workSubCategory: line.workSubCategoryId?.toString() || "",
            taskTitle: line.taskTitle || "",
            taskDescription: line.taskDescription || "",
            quantity: line.quantity || 0,
            uom: line.uomId?.toString() || "",
            rate: line.rate || 0,
            uomName: line.uomName || "",
            towerVilla: line.siteStructureUnitId || "",
            towerVillaName: line.siteStructureUnitName || line.blockName || "",
            floorName: line.floorNo || "",
            flatName: line.siteStructureSubUnitName || "",
            locationName: line.locationName || "",
            budgetLineId: line.budgetLineId || null,
            budgetLineName: line.budgetLineName || "",
            budgetAllocation: line.budgetAllocation || 0,
            allocatedMaterials:
              line.workBoqLineMaterials?.map((material: any) => ({
                materialId: material.materialId?.toString() || "",
                materialName: material.materialName || "Unknown Material",
                quantity: material.allocatedQty || 0,
                categoryId: material.categoryId?.toString() || "",
                category: material.categoryName || "",
                uom: line.uomName || "", // Use UOM from the parent line
                uomName: line.uomName || "",
              })) || [],
            uploadedDocs: line.dms || [],
          })) || [];

        // Map API response to WorkBoqViewData interface
        const workBoqData: WorkBoqViewData = {
          id: id || "",
          boqTitle: apiData.boqTitle || "",
          boqDescription: apiData.boqDescription || "",
          siteName: apiData.siteName || "",
          projectName: apiData.projectTitle || "N/A",
          contractorName: apiData.contractorName || undefined,
          workLevel: apiData.segmentLevel || "",
          materialBoqName: apiData.materialBoqName || "N/A", // Not provided in response
          budgetName: apiData.budgetName || undefined,
          completionAlertsFrom:
            apiData.completionAlertsFrom?.toString() || undefined,
          status: apiData.status || "Draft",
          createdAt: apiData.createdAt || new Date().toISOString(),
          createdBy: apiData.createdByName || "System User",
          submittedAt: apiData.updatedAt || undefined,
          submittedBy: apiData.createdByName || undefined,
          approvals: apiData.workBoqApprovals || [],
          rejectedReason: apiData.rejectedReason || undefined,
          workOrderLines: mappedWorkOrderLines,
          instructions: apiData.instructions || undefined,
          attachments: apiData.attachments || undefined,
          approvalStatus: apiData.approvalStatus || undefined,
          isStrictApproval: apiData.isStrictApproval || false,
        };

        setWorkBoqData(workBoqData);
        message.success("Work BOQ details loaded successfully");
      } else {
        throw new Error(
          response?.message || "Failed to fetch Work BOQ details"
        );
      }
    } catch (error: any) {
      console.error("Error fetching Work BOQ details:", error);
      message.error("Failed to fetch Work BOQ details");
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.message ||
          "Failed to fetch Work BOQ details. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Work order lines table columns
  const [columns] = useState<TableColumnsType<any>>([
    // {
    //   ...Table.EXPAND_COLUMN,
    //   fixed: "left",
    // },
    {
      title: "#",
      key: "#",
      width: 50,
      fixed: "left",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Work Category",
      dataIndex: "workCategory",
      key: "workCategory",
      width: 150,
      fixed: "left",
      render: (text, record) => record.workCategoryName || text || "-",
    },
    // {
    //   title: "Sub Category",
    //   dataIndex: "workSubCategory",
    //   key: "workSubCategory",
    //   width: 150,
    //   render: (text) => text || "-",
    // },
    {
      title: "Task Title",
      dataIndex: "taskTitle",
      key: "taskTitle",
      width: 200,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      render: (text, record) =>
        `${formatDecimal(text)} ${record?.uomName || record?.uom || ""}`,
    },
    {
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
      width: 80,
      render: (text, record) => record?.uomName || text || "-",
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      width: 120,
      render: (text, record) => {
        const rate = record.rate || 0;
        return `₹${Number(rate).toLocaleString()}`;
      },
    },
    {
      title: "Line Total",
      key: "lineTotal",
      width: 140,
      render: (text, record) => {
        const quantity = record.quantity || 0;
        const rate = record.rate || 0;
        const lineTotal = quantity * rate;
        return <strong>₹{Number(lineTotal).toLocaleString()}</strong>;
      },
    },
    {
      title: "Location",
      key: "location",
      width: 150,
      hidden: workBoqData?.workLevel === "Project",
      render: (text, record) => {
        // For Villa level, only show flatName as villa number
        if (workBoqData?.workLevel === "Villa") {
          return record.flatName || "-";
        }

        // For other levels, build location path
        const location = [];
        if (record.towerVilla)
          location.push(record.towerVillaName || record.towerVilla);
        if (record.floorName) location.push(record.floorName);
        if (record.flatName) location.push(record.flatName);
        return (
          <>
            <div className="fw-bold">{location.join(" / ") || "-"}</div>
            <div className="small">
              {(record.locationName && `${record.locationName}`) || ""}
            </div>
          </>
        );
      },
    },
    {
      title: "Materials",
      key: "materials",
      width: 120,
      render: (text, record) => (
        <Badge color="outline-info">
          {record.allocatedMaterials?.length || 0} items
        </Badge>
      ),
    },
    {
      title: "Budget",
      key: "budget",
      width: 120,
      render: (text, record) => (
        <div>
          {record.budgetLineName ? (
            <div style={{ textAlign: "center" }}>
              <Tag color="green" style={{ fontSize: "10px", margin: 0 }}>
                {record.budgetLineName}
              </Tag>
              <div
                style={{ fontSize: "11px", color: "#666", marginTop: "2px" }}
              >
                ₹{formatDecimal(record.budgetAllocation || 0)}
              </div>
            </div>
          ) : (
            <span style={{ color: "#999", fontSize: "11px" }}>No budget</span>
          )}
        </div>
      ),
    },
    {
      title: "Documents",
      key: "documents",
      width: 100,
      render: (text, record) =>
        record.uploadedDocs?.length > 0 ? (
          <CheckCircleOutlined style={{ color: "#28a745" }} />
        ) : (
          <CloseCircleOutlined style={{ color: "#dc3545" }} />
        ),
    },
  ]);

  // Helper: convert base64 data URL to File
  const dataURLToFile = (dataUrl: string, fileName: string): File => {
    const arr = dataUrl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], fileName, { type: mime });
  };

  // Parse level number from approval level string
  const parseLevelNum = (str: string | undefined): number => {
    if (!str) return NaN;
    const match = str.match(/\d+/);
    return match ? parseInt(match[0], 10) : NaN;
  };

  const hasUserAlreadyApproved = workBoqData?.approvals?.some(
    (a: any) =>
      a.userId === currentUser?.id &&
      a.status?.toUpperCase() === "APPROVED"
  );

  const canUserApproveReject = useCallback(() => {
    if (currentUser?.genericRole?.toLowerCase() === "admin") return true;

    const approvals = workBoqData?.approvals || [];
    const userApproval = approvals.find(
      (approval: any) => approval.userId === currentUserId
    );

    if (!userApproval || userApproval.status?.toLowerCase() !== "pending") return false;

    if (!isStrictMode) return true;

    const userLevelNum = parseLevelNum(userApproval.level ?? currentUser?.approvalLevel);
    const pendingApprovals = approvals
      .filter((a: any) => a.status?.toLowerCase() === "pending")
      .sort((a: any, b: any) => parseLevelNum(a.level) - parseLevelNum(b.level));

    if (pendingApprovals.length === 0) return false;

    const firstPending = pendingApprovals[0];
    return userApproval.userId === firstPending.userId &&
      userLevelNum === parseLevelNum(firstPending.level);
  }, [currentUser, currentUserId, workBoqData?.approvals, isStrictMode]);

  const getPendingStatusMessage = () => {
    const approvals = workBoqData?.approvals || [];
    const pendingApprovals = approvals
      .filter((a: any) => a.status?.toLowerCase() === "pending")
      .sort((a: any, b: any) => parseLevelNum(a.level) - parseLevelNum(b.level));
    if (pendingApprovals.length === 0) return "No pending approvals.";
    const next = pendingApprovals[0];
    return `Waiting for ${next.userName || "approver"} (${next.level || "N/A"}) to approve first.`;
  };

  const handleApprove = async (signatureData?: string) => {
    try {
      setDisableApprove(true);

      if (!id) {
        message.error("Work BOQ ID is required");
        return;
      }

      const files: File[] = [];
      if (signatureData) {
        files.push(dataURLToFile(signatureData, "signature.png"));
      }

      const workBoqId = parseInt(id);
      const response: any = await workBoqApis.approveWorkBoq(workBoqId, files);

      if (response && response.status) {
        setWorkBoqData((prev: any) =>
          prev ? { ...prev, status: "Approved" as const } : null
        );
        Swal.fire(
          "Approved!",
          response.message || "Work BOQ has been approved successfully",
          "success"
        ).then(() => {
          const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
          navigate(`/work-boq-list${flowTypeQuery}`);
        });
      } else {
        throw new Error(response?.message || "Failed to approve Work BOQ");
      }
    } catch (error: any) {
      console.error("Error approving Work BOQ:", error);
      Swal.fire(
        "Error!",
        error.message || "Failed to approve Work BOQ. Please try again.",
        "error"
      );
    } finally {
      setDisableApprove(false);
    }
  };

  const handleReject = async (rejectedReason: string, signatureData?: string) => {
    try {
      setDisableReject(true);

      if (!id) {
        message.error("Work BOQ ID is required");
        return;
      }

      const files: File[] = [];
      if (signatureData) {
        files.push(dataURLToFile(signatureData, "signature.png"));
      }

      const workBoqId = parseInt(id);
      const response: any = await workBoqApis.rejectWorkBoq(workBoqId, rejectedReason.trim(), files);

      if (response && response.status) {
        setWorkBoqData((prev: any) =>
          prev ? { ...prev, status: "Rejected" as const, rejectedReason } : null
        );
        Swal.fire(
          "Rejected!",
          response.message || "Work BOQ has been rejected successfully",
          "success"
        ).then(() => {
          const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
          navigate(`/work-boq-list${flowTypeQuery}`);
        });
      } else {
        throw new Error(response?.message || "Failed to reject Work BOQ");
      }
    } catch (error: any) {
      console.error("Error rejecting Work BOQ:", error);
      Swal.fire(
        "Error!",
        error.message || "Failed to reject Work BOQ. Please try again.",
        "error"
      );
    } finally {
      setDisableReject(false);
    }
  };

  const showApproveModal = () => {
    setSignature({ isActive: false, label: "Authorised Signatory", image: null });
    setActiveSignatureContext("APPROVE");
    setIsModalApproveVisible(true);
  };

  const showRejectModal = () => {
    setPendingRejectReason("");
    form.resetFields(["reason"]);
    setSignature({ isActive: false, label: "Authorised Signatory", image: null });
    setActiveSignatureContext("REJECT");
    setIsModalRejectedVisible(true);
  };

  const openApproveConfirmSwal = async () => {
    if (!signature.image) {
      Swal.fire({ icon: "info", title: "Signature Required", text: "Please add your signature before approving." });
      return;
    }
    setIsModalApproveVisible(false);
    const res = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to approve this Work BOQ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Approve it!",
      cancelButtonText: "No, keep it",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });
    if (res.isConfirmed) {
      try {
        await handleApprove(signature.image);
      } finally {
        setSignature({ isActive: false, label: "Authorised Signatory", image: null });
        setActiveSignatureContext(null);
        setDisableApprove(false);
      }
    } else {
      setIsModalApproveVisible(true);
      setDisableApprove(false);
    }
  };

  const openRejectConfirmSwal = async () => {
    if (!pendingRejectReason || !pendingRejectReason.trim()) {
      Swal.fire({ icon: "info", title: "Reason Required", text: "Please enter a rejection reason." });
      return;
    }
    if (!signature.image) {
      Swal.fire({ icon: "info", title: "Signature Required", text: "Please add your signature before rejecting." });
      return;
    }
    setIsModalRejectedVisible(false);
    const res = await Swal.fire({
      title: "Confirm Rejection?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Reject it!",
      cancelButtonText: "Cancel",
      allowOutsideClick: false,
      allowEscapeKey: false,
    });
    if (res.isConfirmed) {
      try {
        await handleReject(pendingRejectReason, signature.image);
      } finally {
        setSignature({ isActive: false, label: "Authorised Signatory", image: null });
        setActiveSignatureContext(null);
        setPendingRejectReason("");
        setDisableReject(false);
      }
    } else {
      setIsModalRejectedVisible(true);
      setDisableReject(false);
    }
  };

  const ApprovalTimelineContent = () => {
    const approvalsData = workBoqData?.approvals;

    const isStrictMode = workBoqData?.isStrictApproval || false;
    if (!approvalsData || approvalsData?.length === 0) {
      return <Badge color="outline-danger">No</Badge>;
    }

    return (
      <Popover
        content={
          <Timeline className="approval-timeline">
            {approvalsData?.map((approval: any, index: number) => {
              const isApproved = approval.status === "Approved";
              const isPending = approval.status === "Pending";
              return (
                <Timeline.Item
                  key={index}
                  dot={
                    isApproved ? (
                      <CheckCircleOutlined style={{ color: "green" }} />
                    ) : isPending ? (
                      <CloseCircleOutlined style={{ color: "orange" }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: "red" }} />
                    )
                  }
                >
                  {approval?.userName}
                  <div className="designation">{approval?.roleName}</div>
                  <div className="designation">{approval?.approvalStage}</div>
                  {approval?.actionDate && (
                    <div className="designation">
                      {dayjs(approval?.actionDate).format("DD-MMM-YYYY")}
                    </div>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        }
        title={<span style={{ color: "#2263b3" }}>Approval Hierarchy</span>}
      >
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer"
        }}>
          <span
            style={{
              color: "#2263b3",
            }}
          >
            <Badge color="outline-success">Yes</Badge>
          </span>

          {isStrictMode && (
            <Badge
              color="warning"
              style={{
                fontSize: "11px",
                cursor: "pointer",
                marginLeft: "4px"
              }}
              title="Sequential approvals required, Approvals must follow the sequence"
            >
              Strict
            </Badge>
          )}
        </div>
      </Popover>
    );
  };

  const getStatusMapper = (status: string) => {
    const statusMap: { [key: string]: { title: string; color: string } } = {
      Draft: { title: "Draft", color: "secondary" },
      "Pending Approval": { title: "Pending Approval", color: "warning" },
      Approved: { title: "Approved", color: "success" },
      Rejected: { title: "Rejected", color: "danger" },
    };
    return statusMap[status] || { title: status, color: "secondary" };
  };

  if (isLoading) {
    return (
      <>
        <Head title="Work BOQ Details" />
        <Content>
          <Flex align="center" gap="middle" className="pageLoader">
            <Spin size="large" className="m-auto" />
          </Flex>
        </Content>
      </>
    );
  }

  if (!workBoqData) {
    return (
      <>
        <Head title="Work BOQ Not Found" />
        <Content>
          <div className="text-center py-5">
            <Empty description="Work BOQ not found" />
          </div>
        </Content>
      </>
    );
  }

  const statusInfo = getStatusMapper(workBoqData.status);

  return (
    <>
      <Head title={`Work BOQ - ${workBoqData.boqTitle}`} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div className="card-inner card-inner-lg">
              {sm && mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => setSm(!sm)}
                ></div>
              )}
              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">
                      <span>
                        {workBoqData.boqTitle} | {statusInfo.title}
                      </span>
                    </BlockTitle>
                  </BlockHeadContent>
                  <div className="d-flex">
                    <div className="actions clearfix">
                      <ul
                        className="m-auto d-flex align-items-center justify-content-center p-0"
                        style={{
                          flexDirection: "row-reverse",
                        }}
                      >
                        <li className="pt-0 pb-0">
                          <Dropdown
                            isOpen={shareOptionsToggle}
                            toggle={() => {
                              setShareOptionsToggle(!shareOptionsToggle);
                            }}
                          >
                            {/* <DropdownToggle className="btn btn-white btn-outline-light">
                              <Icon name="share"></Icon>
                              <span>Share</span>
                            </DropdownToggle> */}
                            <DropdownMenu>
                              {/* <DropdownItem onClick={() => {}}>
                                <Icon name="mail"></Icon>
                                <span>Send Mail</span>
                              </DropdownItem> */}
                              {/* <DropdownItem onClick={() => {}}>
                                <Icon name="download"></Icon>
                                <span>Export PDF</span>
                              </DropdownItem> */}
                            </DropdownMenu>
                          </Dropdown>
                        </li>
                        {/* <li className="pt-0 pb-0">
                          <WorkBoqPdfGenerator
                            workBoqData={workBoqData}
                            orgLogo={currentUser?.orgLogo}
                            orgName={currentUser?.orgName}
                            getPdfUrl={() => {}}
                          />
                        </li> */}

                        <li className="pt-0 pb-0">
                          <Button
                            className="bg-white"
                            outline
                            color="light"
                            onClick={() => {
                              // Check if user came from notifications
                              if (location.state?.fromNotifications) {
                                navigate("/notifications");
                                return;
                              }
                              // Check if user came from approvals page
                              if (location.state?.fromPage === "approvals") {
                                const searchParams = new URLSearchParams(
                                  location.search
                                );
                                const flowType = searchParams.get("flowType");
                                const flowTypeQuery = flowType
                                  ? `?flowType=${flowType}`
                                  : "";
                                navigate(`/work-boq-approvals${flowTypeQuery}`);
                                return;
                              }
                              const searchParams = new URLSearchParams(
                                location.search
                              );
                              const flowType = searchParams.get("flowType");
                              const flowTypeQuery = flowType
                                ? `?flowType=${flowType}`
                                : "";
                              navigate(`/work-boq-list${flowTypeQuery}`);
                            }}
                          >
                            <Icon name="arrow-left" />
                            <span>Back</span>
                          </Button>
                        </li>
                      </ul>
                    </div>

                    <ul className="nk-tb-actions ms-2">
                      <li>
                        <UncontrolledDropdown>
                          <DropdownToggle
                            color="primary"
                            className="btn-icon"
                            style={{ padding: "8px 12px" }}
                          >
                            <MoreOutlined style={{ fontSize: "18px" }} />
                          </DropdownToggle>
                          <DropdownMenu end>
                            <ul className="link-list-opt no-bdr">
                              {["Pending"].includes(workBoqData?.approvalStatus ?? "") &&
                                allowedActions?.approve === 1 &&
                                !hasUserAlreadyApproved && (
                                  <>
                                    {canUserApproveReject() ? (
                                      <>
                                        <li
                                          onClick={(e) => {
                                            e.preventDefault();
                                            showApproveModal();
                                          }}
                                        >
                                          <Link to="#" className="text-success">
                                            <Icon name="check" /> {disableApprove ? "Approve..." : "Approve"}
                                          </Link>
                                        </li>
                                        <li
                                          onClick={(e) => {
                                            e.preventDefault();
                                            showRejectModal();
                                          }}
                                        >
                                          <Link to="#" className="text-danger">
                                            <Icon name="cross" /> {disableReject ? "Reject..." : "Reject"}
                                          </Link>
                                        </li>
                                      </>
                                    ) : (
                                      <>
                                        <li
                                          style={{ cursor: "not-allowed", opacity: 0.6 }}
                                          onClick={(e) => e.preventDefault()}
                                        >
                                          <span className="text-success d-flex align-items-center justify-content-between px-2">
                                            <span><Icon name="check" /> Approve</span>
                                            <Popover
                                              content={<div style={{ padding: 8, maxWidth: 300 }}><p style={{ margin: 0 }}>{getPendingStatusMessage()}</p></div>}
                                              title="Approval Status"
                                              trigger="hover"
                                            >
                                              <InfoCircleOutlined style={{ fontSize: 16, cursor: "pointer", color: "#2263b3" }} />
                                            </Popover>
                                          </span>
                                        </li>
                                        <li
                                          style={{ cursor: "not-allowed", opacity: 0.6 }}
                                          onClick={(e) => e.preventDefault()}
                                        >
                                          <span className="text-danger px-2">
                                            <Icon name="cross" /> Reject
                                          </span>
                                        </li>
                                      </>
                                    )}
                                  </>
                                )}
                            </ul>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </li>
                    </ul>
                  </div>
                  <BlockHeadContent className="align-self-start d-lg-none">
                    <Button
                      className={`toggle btn btn-icon btn-trigger mt-n1 ${sm ? "active" : ""
                        }`}
                      onClick={() => setSm(!sm)}
                    >
                      <Icon name="menu-alt-r" />
                    </Button>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>

              <Alert
                color={"danger"}
                isOpen={workBoqData.status === "Rejected"}
              >
                <strong>Rejected: </strong>
                {workBoqData.rejectedReason || "No reason provided"}
              </Alert>

              <div className="content clearfix">
                <AntCollapse defaultActiveKey={["1", "2", "3"]}>
                  <Panel
                    header={
                      <div className="d-flex align-items-center justify-content-between">
                        <>General Information</>
                      </div>
                    }
                    key="1"
                  >
                    <Col className="justify-start w-100 flex-wrap mt-1 p-1">
                      <div className="w-30 border-end">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">BOQ Title</div>
                          <div className="data-value w-70">
                            {workBoqData.boqTitle || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Site</div>
                          <div className="data-value w-70">
                            {workBoqData.siteName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Project</div>
                          <div className="data-value w-70">
                            {workBoqData.projectName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Approvals</div>
                          <div className="data-value w-70">
                            <ApprovalTimelineContent />
                          </div>
                        </div>
                      </div>
                      <div className="w-30 border-end paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">
                            {isClientFlow ? "Client" : "Contractor"}
                          </div>
                          <div className="data-value w-70">
                            {workBoqData.contractorName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Material BOQ</div>
                          <div className="data-value w-70">
                            {workBoqData.materialBoqName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Budget</div>
                          <div className="data-value w-70">
                            {workBoqData.budgetName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Work Level</div>
                          <div className="data-value w-70">
                            <Badge color="outline-primary">
                              {workBoqData.workLevel || "--"}
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Status</div>
                          <div className="data-value w-70">
                            <Badge color={`outline-${statusInfo.color}`}>
                              {statusInfo.title}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="w-30 paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Description</div>
                          <div className="data-value w-70">
                            <Popover
                              content={
                                <div>
                                  <p>{workBoqData.boqDescription}</p>
                                </div>
                              }
                              title={"BOQ Description"}
                            >
                              {workBoqData.boqDescription?.length > 30 ? (
                                <span
                                  style={{
                                    color: "#2263b3",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                  }}
                                >
                                  {workBoqData.boqDescription.substring(0, 30)}
                                  ...
                                </span>
                              ) : (
                                workBoqData.boqDescription || "--"
                              )}
                            </Popover>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">
                            Completion Alert
                          </div>
                          <div className="data-value w-70">
                            <Badge color="outline-info">
                              {workBoqData.completionAlertsFrom
                                ? `${workBoqData.completionAlertsFrom}%`
                                : "Not Set"}
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Created By</div>
                          <div className="data-value w-70">
                            {workBoqData.createdBy || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Created At</div>
                          <div className="data-value w-70">
                            {dayjs(workBoqData.createdAt).format("DD-MMM-YYYY")}
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Panel>

                  <Panel header="Work Order Lines" key="2">
                    <div className="table-responsive poTable">
                      <div
                        style={{
                          width: screenSize - 170 + "px",
                          margin: "auto",
                        }}
                      >
                        {!workBoqData.workOrderLines ||
                          workBoqData.workOrderLines.length === 0 ? (
                          <div className="text-center py-5">
                            <Empty description="No work order lines found" />
                          </div>
                        ) : (
                          <Table
                            className="customTable"
                            columns={columns}
                            dataSource={workBoqData.workOrderLines}
                            rowKey="id"
                            scroll={{ x: 1000 }}
                            expandable={{
                              expandedRowRender: (record: any) => (
                                <div className="expandableBody">
                                  <div style={{ flex: 1, textAlign: "left" }}>
                                    {record.taskDescription && (
                                      <div className="mb-3">
                                        <strong>Task Description:</strong>
                                        <p style={{ margin: "5px 0" }}>
                                          {record.taskDescription}
                                        </p>
                                      </div>
                                    )}

                                    {record.allocatedMaterials?.length > 0 && (
                                      <div className="mb-3">
                                        <strong>
                                          Allocated Materials (
                                          {record.allocatedMaterials.length}):
                                        </strong>
                                        <div className="mt-2">
                                          <Table
                                            className="customTable"
                                            columns={[
                                              {
                                                title: "Material",
                                                key: "material",
                                                width: "40%",
                                                render: (_, material: any) => (
                                                  <div>
                                                    <div className="fw-medium">
                                                      {typeof material ===
                                                        "string"
                                                        ? material
                                                        : material?.materialName ||
                                                        material?.name ||
                                                        "Unknown Material"}
                                                    </div>
                                                    {material?.category && (
                                                      <Tag color="blue">
                                                        {material.category}
                                                      </Tag>
                                                    )}
                                                  </div>
                                                ),
                                              },
                                              {
                                                title: "Quantity",
                                                key: "quantity",
                                                width: "20%",
                                                render: (_, material: any) => (
                                                  <span>
                                                    {material?.quantity || 0}{" "}
                                                    {material?.uomName ||
                                                      material?.uom ||
                                                      ""}
                                                  </span>
                                                ),
                                              },
                                              {
                                                title: "UOM",
                                                dataIndex: "uom",
                                                key: "uom",
                                                width: "15%",
                                                render: (
                                                  uom: string,
                                                  material: any
                                                ) => (
                                                  <Tag color="geekblue">
                                                    {material?.uomName ||
                                                      uom ||
                                                      "-"}
                                                  </Tag>
                                                ),
                                              },
                                            ]}
                                            dataSource={
                                              record.allocatedMaterials
                                            }
                                            rowKey={(material, index) =>
                                              material?.materialId || index
                                            }
                                            pagination={false}
                                            size="small"
                                            bordered
                                          />
                                        </div>
                                      </div>
                                    )}

                                    {record.budgetLineName && (
                                      <div className="mb-3">
                                        <strong>Budget Allocation:</strong>
                                        <div className="mt-2">
                                          <Tag color="blue">
                                            Budget Line: {record.budgetLineName}
                                          </Tag>
                                          <Tag color="cyan">
                                            Allocated Amount: ₹
                                            {formatDecimal(
                                              record.budgetAllocation || 0
                                            )}
                                          </Tag>
                                        </div>
                                      </div>
                                    )}

                                    {(record.towerVilla ||
                                      record.floorName ||
                                      record.flatName) && (
                                        <div className="mb-3">
                                          <strong>Location Details:</strong>
                                          <div style={{ marginTop: 5 }}>
                                            {record.towerVilla && (
                                              <Tag color="green">
                                                {workBoqData.workLevel === "Villa"
                                                  ? "Villa"
                                                  : "Tower/Block"}
                                                :{" "}
                                                {record.towerVillaName ||
                                                  record.towerVilla}
                                              </Tag>
                                            )}
                                            {record.floorName && (
                                              <Tag color="orange">
                                                Floor: {record.floorName}
                                              </Tag>
                                            )}
                                            {record.flatName && (
                                              <Tag color="purple">
                                                {workBoqData?.workLevel ===
                                                  "Villa"
                                                  ? "Villa"
                                                  : "Flat"}
                                                : {record.flatName}
                                              </Tag>
                                            )}
                                            {record.locationName && (
                                              <Tag color="geekblue">
                                                Location: {record.locationName}
                                              </Tag>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                    {record.uploadedDocs?.length > 0 && (
                                      <div>
                                        <strong>Documents:</strong>
                                        <div style={{ marginTop: 8 }}>
                                          {record.uploadedDocs.map(
                                            (doc: any, index: number) => (
                                              <div
                                                key={index}
                                                style={{
                                                  marginLeft: "10px",
                                                  marginBottom: "4px",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "4px",
                                                }}
                                              >
                                                <PaperClipOutlined />
                                                <span>{doc.fileName}</span>
                                                {doc.filePath && (
                                                  <a
                                                    href={doc.filePath}
                                                    download={doc.fileName}
                                                    style={{
                                                      color: "#1890ff",
                                                      textDecoration: "none",
                                                      padding: "2px",
                                                      borderRadius: "2px",
                                                      fontSize: "14px",
                                                      display: "inline-flex",
                                                      alignItems: "center",
                                                      cursor: "pointer",
                                                    }}
                                                    onMouseEnter={(e) => {
                                                      e.currentTarget.style.backgroundColor =
                                                        "#f0f0f0";
                                                    }}
                                                    onMouseLeave={(e) => {
                                                      e.currentTarget.style.backgroundColor =
                                                        "transparent";
                                                    }}
                                                    onClick={(e) => {
                                                      // Force download instead of opening in new tab
                                                      e.preventDefault();
                                                      const link =
                                                        document.createElement(
                                                          "a"
                                                        );
                                                      link.href = doc.filePath;
                                                      link.download =
                                                        doc.fileName;
                                                      link.target = "_blank";
                                                      document.body.appendChild(
                                                        link
                                                      );
                                                      link.click();
                                                      document.body.removeChild(
                                                        link
                                                      );
                                                    }}
                                                  >
                                                    <DownloadOutlined />
                                                  </a>
                                                )}
                                              </div>
                                            )
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ),
                            }}
                            bordered
                            size="small"
                            pagination={false}
                          />
                        )}
                      </div>

                      {workBoqData.workOrderLines?.length > 0 && (
                        <div
                          className="mt-3 p-3 bg-light rounded"
                          style={{
                            maxWidth: "400px",
                            marginLeft: "auto",
                            marginRight: "20px",
                          }}
                        >
                          <h6 className="mb-3">Summary</h6>

                          {/* Summary Info */}
                          <div className="d-flex justify-content-between mb-2">
                            <span>Total Work Lines:</span>
                            <strong>{workBoqData.workOrderLines.length}</strong>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span>Total Quantity:</span>
                            <strong>
                              {formatDecimal(
                                workBoqData.workOrderLines.reduce(
                                  (total: number, line: any) =>
                                    total + (Number(line.quantity) || 0),
                                  0
                                )
                              )}
                            </strong>
                          </div>
                          <div className="d-flex justify-content-between mb-2">
                            <span>Work Categories:</span>
                            <strong>
                              {
                                new Set(
                                  workBoqData.workOrderLines.map(
                                    (line: any) => line.workCategory
                                  )
                                ).size
                              }
                            </strong>
                          </div>
                          {workBoqData.workOrderLines.some(
                            (line: any) => line.budgetAllocation
                          ) && (
                              <div className="d-flex justify-content-between mb-2">
                                <span>Total Budget Allocation:</span>
                                <strong>
                                  ₹
                                  {workBoqData.workOrderLines.reduce(
                                    (total: number, line: any) =>
                                      total +
                                      (Number(line.budgetAllocation) || 0),
                                    0
                                  )}
                                </strong>
                              </div>
                            )}

                          <hr className="my-2" />

                          {/* Gross Total */}
                          <div className="d-flex justify-content-between">
                            <strong style={{ fontSize: "16px" }}>
                              Gross Total:
                            </strong>
                            <strong
                              style={{ fontSize: "16px", color: "#28a745" }}
                            >
                              ₹
                              {workBoqData.workOrderLines.reduce(
                                (sum: number, line: any) =>
                                  sum +
                                  (Number(
                                    (line.quantity || 0) * (line.rate || 0)
                                  ) || 0),
                                0
                              )}
                            </strong>
                          </div>
                        </div>
                      )}

                      {/* Old summary code below - keeping for reference but hidden */}
                      {false && workBoqData?.workOrderLines?.length > 0 && (
                        <Row style={{ margin: "20px" }}>
                          <Col className="justify-end">
                            <div className="w-40"></div>
                          </Col>
                        </Row>
                      )}
                    </div>
                  </Panel>
                </AntCollapse>
              </div>
            </div>
          </div>
        </Card>
      </Content>

      <Modal
        title="Instructions"
        visible={instructionsVisible}
        footer={null}
        onCancel={() => setInstructionsVisible(false)}
        width="60%"
        bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        {workBoqData.instructions ? (
          <pre>{workBoqData.instructions}</pre>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <Empty description="No instructions provided for this work BOQ" />
          </div>
        )}
      </Modal>

      {/* Approve Signature Modal */}
      <Modal
        title="Approve Work BOQ"
        visible={isModalApproveVisible}
        onOk={openApproveConfirmSwal}
        onCancel={() => {
          setIsModalApproveVisible(false);
          setSignature({ isActive: false, label: "Authorised Signatory", image: null });
          setActiveSignatureContext(null);
        }}
        okText="Submit"
        cancelText="Cancel"
        width={600}
      >
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span style={{ fontSize: 14, fontWeight: 600 }}>Signature</span>
          </div>

          {signature.image ? (
            <>
              <div
                className="border mb-3 d-flex justify-content-center align-items-center"
                style={{ padding: 16, borderRadius: 8, background: "#fff" }}
              >
                <img src={signature.image} alt="Signed" style={{ maxWidth: 220, maxHeight: 120, objectFit: "contain" }} />
              </div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Button size="sm" color="outline-secondary" onClick={() => setSignature({ isActive: true, label: "Authorised Signatory", image: null })}>
                  Reset
                </Button>
              </div>
            </>
          ) : (
            <div style={{ width: "100%", minHeight: 220, border: "2px dashed #dee2e6", borderRadius: 8, backgroundColor: "#f8f9fa", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
              <div style={{ display: "flex", gap: 12, justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
                <ImageUploadCropper
                  onOpenCrop={() => setIsModalApproveVisible(false)}
                  onCloseCrop={() => setIsModalApproveVisible(true)}
                  onSave={(data: string) => setSignature((prev: any) => ({ ...prev, image: data }))}
                >
                  <Button color="primary" size="sm" outline style={{ minWidth: 140 }} onClick={() => setActiveSignatureContext("APPROVE")}>
                    <Icon name="upload-cloud" className="me-1" />
                    Upload Image
                  </Button>
                </ImageUploadCropper>
                <Button color="outline-primary" size="sm" style={{ minWidth: 140 }} onClick={() => { setIsModalApproveVisible(false); setShowSignatureModal(true); }}>
                  <Icon name="pen" className="me-1" />
                  Signature Pad
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Reject Signature Modal */}
      <Modal
        title="Reject Work BOQ"
        visible={isModalRejectedVisible}
        onOk={() => {
          if (!pendingRejectReason || !pendingRejectReason.trim()) {
            Swal.fire({ icon: "info", title: "Reason Required", text: "Please enter a rejection reason." });
            return;
          }
          openRejectConfirmSwal();
        }}
        onCancel={() => {
          setIsModalRejectedVisible(false);
          setSignature({ isActive: false, label: "Authorised Signatory", image: null });
          setActiveSignatureContext(null);
          setPendingRejectReason("");
          form.resetFields(["reason"]);
        }}
        okText="Submit"
        cancelText="Cancel"
        width={600}
      >
        <Form form={form} layout="vertical" name="rejectBoqForm">
          <Form.Item
            name="reason"
            label="Reason for Rejection"
            rules={[{ required: true, message: "Please input your reason!" }]}
          >
            <Input.TextArea rows={3} placeholder="Type your reason here..." value={pendingRejectReason} onChange={(e) => setPendingRejectReason(e.target.value)} />
          </Form.Item>

          <div className="mt-4">
            <strong style={{ fontSize: "16px" }}>Signature</strong>
          </div>

          {signature.image ? (
            <>
              <div className="signatueImgWrapper border mb-3 p-3 text-center">
                <img src={signature.image} alt="Signed" style={{ width: "180px", height: "auto" }} />
              </div>
              <div className="d-flex justify-content-end mb-3">
                <Button size="sm" color="outline-secondary" className="me-2" onClick={() => setSignature({ isActive: true, label: "Authorised Signatory", image: null })}>
                  Reset
                </Button>
              </div>
            </>
          ) : (
            <div style={{ minHeight: "200px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "15px", border: "2px dashed #dee2e6", borderRadius: "8px", backgroundColor: "#f8f9fa" }}>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
                <ImageUploadCropper
                  onOpenCrop={() => setIsModalRejectedVisible(false)}
                  onCloseCrop={() => setIsModalRejectedVisible(true)}
                  onSave={(data: string) => setSignature((prev: any) => ({ ...prev, image: data }))}
                >
                  <Button color="primary" size="sm" outline onClick={() => setActiveSignatureContext("REJECT")}>
                    <Icon name="upload-cloud" className="me-1" />
                    Upload Image
                  </Button>
                </ImageUploadCropper>
                <Button color="outline-primary" size="sm" onClick={() => { setIsModalRejectedVisible(false); setShowSignatureModal(true); }}>
                  <Icon name="pen" className="me-1" />
                  Signature Pad
                </Button>
              </div>
            </div>
          )}
        </Form>
      </Modal>

      {showSignatureModal && (
        <SignatureModal
          modal={showSignatureModal}
          toggle={() => {
            setShowSignatureModal(false);
            if (activeSignatureContext === "APPROVE") {
              setIsModalApproveVisible(true);
            } else if (activeSignatureContext === "REJECT") {
              setIsModalRejectedVisible(true);
            }
          }}
          onSave={(data: string) => {
            setSignature((prev: any) => ({ ...prev, image: data }));
            setShowSignatureModal(false);
            if (activeSignatureContext === "APPROVE") {
              setIsModalApproveVisible(true);
            } else if (activeSignatureContext === "REJECT") {
              setIsModalRejectedVisible(true);
            }
          }}
        />
      )}
    </>
  );
};

export default WorkBoqView;
